import BundleMainProduct from '../BundleProduct/main';
import { BadgeData, IEmblemBadge } from 'components/Badge/types';
import { StyledMainSubBundle, SUB_PRODUCTS_X_PADDING } from '../style';
import { BundleProps } from 'utils/types';
import { Box, Stack } from '@mui/material';
import { BundlePrice } from 'components/BundlePrice';
import {
  EBadgePosition,
  EBadgeType,
  EProductPriority,
  EProductType,
} from 'utils/enums';
import Badge from 'components/Badge/CornerBadge';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';
import { DefaultSaleBadge } from 'components/Badge/Sale/DefaultSaleBadge';
import { moveProductGrid } from 'utils';
import { useGetBadge } from 'utils/useGetBadge';
import BundleSubProduct from '../BundleProduct/sub';

export const MainSubBundle: React.FC<BundleProps> = ({
  backgroundSrc,
  backgroundColor = { colorOne: '#000088' },
  prices,
  productList = [
    {
      textFontColorHex: '#ffabff',
      images: {
        product:
          'https://animal-kingdom-media.s3.amazonaws.com/asset-2.png?a=a',
      },
      quantity: 100,
      prefix: '',
      suffix: '',
      priority: EProductPriority.MAIN,
      quantityDisplay: '100',
      productType: EProductType.QUANTITY,
      publisherProductId: '',
    },
  ],
  bundleBorderColor,
  availability,
  bundleButtonTextColor,
  fontFamily,
  badges = [],
  emblemBadges = [],
  salePercentage = 0,
  bundleRef,
  salePercentageDisplayType,
  borderRadius,
  inMobileMock = false,
  loaded,
  tooltipOpenEventHandler,
}) => {
  const checkScreenSize = () => {
    return inMobileMock ||
      isIOS ||
      isAndroid ||
      isMobile ||
      window.innerWidth < 700
      ? 'regular'
      : 'big';
  };
  const subItemsGrid = useRef<HTMLDivElement>(null);
  const [isDoneScrolling, setIsDoneScrolling] = useState(false);
  const [bundleSize, setBundleSize] = useState(checkScreenSize());
  const diffInSize = '1.2'; // 20%
  const [showScroller, setShowScroller] = useState(false);
  const [emblemBadgeOld, setEmblemBadgeOld] = useState<BadgeData | null>(null);
  const emblemBadgeNew = useGetBadge<IEmblemBadge>(emblemBadges);

  useEffect(() => {
    if (badges?.[0]) {
      setEmblemBadgeOld(badges[0]);
    }
  }, [badges]);

  const emblemBadge = emblemBadgeNew ?? emblemBadgeOld;

  const moveGrid = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation();

    const subItemsGridElement = subItemsGrid?.current;
    if (subItemsGridElement as HTMLDivElement) {
      moveProductGrid({
        subItemsGridElement: subItemsGridElement as HTMLDivElement,
        isDoneScrolling,
        setIsDoneScrolling,
      });
    }
  };

  const availabilityText =
    availability?.remaining && availability?.total
      ? `${availability.remaining}/${availability.total}`
      : '';

  // Trigger re-rendering when screen bundleSize changing
  useEffect(() => {
    let timeoutId: any;

    const shouldShowScrollbar = () => {
      setShowScroller(
        subItemsGrid?.current
          ? subItemsGrid.current.scrollWidth -
              subItemsGrid.current.clientWidth >
              SUB_PRODUCTS_X_PADDING * 2
          : false,
      );
    };

    shouldShowScrollbar();

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setBundleSize(checkScreenSize());
      }, 100);

      shouldShowScrollbar();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [subItemsGrid.current, loaded]);

  const marginRightMainProduct = useMemo(() => {
    if (salePercentage === 0) {
      return 0;
    }

    if (productList.length > 0 && productList[0]?.strokedSaleQuantity) {
      return 14;
    }
    return 8;
  }, [productList, salePercentage]);

  const marginLeftMainProduct = useMemo(() => {
    if (emblemBadge) {
      return 48;
    }
    return 0;
  }, [emblemBadge]);

  return (
    <StyledMainSubBundle
      bundleBorderColor={bundleBorderColor}
      backgroundSrc={backgroundSrc}
      fontFamily={fontFamily}
      backgroundColor={backgroundColor}
      textColor={bundleButtonTextColor}
      className={
        'main-sub-bundle main-sub-bundle--' +
        bundleSize +
        (inMobileMock ? ' bundle--iphone-mock' : '')
      }
      ref={bundleRef}
      borderRadius={borderRadius}
      diffInSize={diffInSize}
    >
      <Box className="container">
        {productList.length > 0 ? (
          <Box className="products">
            {showScroller && (
              <Stack className="scroller">
                <button onClick={(e) => moveGrid(e)}>
                  {isDoneScrolling ? (
                    <ArrowBackIosNewIcon
                      sx={{
                        color: 'white',
                        fontSize: 16,
                      }}
                    />
                  ) : (
                    <ArrowForwardIosIcon
                      sx={{
                        color: 'white',
                        fontSize: 16,
                      }}
                    />
                  )}
                </button>
              </Stack>
            )}
            <Box
              className="main-product"
              mr={`${marginRightMainProduct}px`}
              ml={`${marginLeftMainProduct}px`}
              maxWidth={`calc(100% - ${
                marginRightMainProduct + marginLeftMainProduct
              }px)`}
              width={`calc(100% - ${
                marginRightMainProduct + marginLeftMainProduct
              }px)`}
            >
              <BundleMainProduct
                quantityDisplay={productList[0].quantityDisplay}
                strokedSaleQuantity={productList[0].strokedSaleQuantity}
                publisherProductId={productList[0].publisherProductId}
                images={productList[0].images}
                quantity={productList[0].quantity}
                salePercentage={salePercentage}
                salePercentageDisplayType={salePercentageDisplayType}
                rarityProductInfo={productList[0].rarityProductInfo}
                badgeText={productList[0].badgeText}
                name={productList[0].name}
                tooltipOpenEventHandler={tooltipOpenEventHandler}
                productType={productList[0].productType}
              />
            </Box>
            <Box className="sub-items-grid" ref={subItemsGrid} rowGap={'4px'}>
              {productList.length > 1 &&
                productList?.slice(1).map((product, i) => (
                  <Stack justifyContent="center" alignItems="center">
                    <BundleSubProduct
                      key={i}
                      textFontColorHex={product.textFontColorHex}
                      quantityDisplay={product.quantityDisplay}
                      productType={product.productType}
                      images={product.images}
                      quantity={product.quantity}
                      publisherProductId={product.publisherProductId}
                      rarityProductInfo={product.rarityProductInfo}
                      badgeText={product.badgeText}
                      name={product.name}
                      index={i}
                      tooltipOpenEventHandler={tooltipOpenEventHandler}
                    />
                  </Stack>
                ))}
            </Box>
          </Box>
        ) : (
          <Box className="products" style={{ height: '204px' }}></Box>
        )}
        <BundlePrice
          prices={prices}
          type="mainSub"
          borderRadius={`0 0 ${borderRadius || 15}px ${borderRadius || 15}px`}
          whiteStripe={true}
          isPreview={inMobileMock}
          textShadow="0px 2px 0px #000000"
        />
      </Box>
      {salePercentage > 0 && (
        <DefaultSaleBadge
          salePercentage={salePercentage}
          type={EBadgeType.SALE}
          position={EBadgePosition.RIGHT}
          salePercentageDisplayType={salePercentageDisplayType}
        />
      )}
      {emblemBadge && (
        <Badge
          emblem={emblemBadgeNew ?? emblemBadgeOld?.emblem}
          type={EBadgeType.EMBLEM}
          {...emblemBadge}
          position={EBadgePosition.LEFT}
        />
      )}
      {availabilityText && (
        <span className="availability quantity">{availabilityText}</span>
      )}
    </StyledMainSubBundle>
  );
};
